import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ActionIcon,
  AppShell,
  Avatar,
  Box,
  Burger,
  Button,
  Divider,
  Group,
  Image,
  Loader,
  Menu,
  NavLink,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import {
  BarChart3,
  Bell,
  CalendarCheck,
  ClipboardCheck,
  ClipboardList,
  ClipboardPlus,
  Cog,
  CreditCard,
  FileText,
  FolderKanban,
  Fuel,
  Home,
  ListChecks,
  LogOut,
  Megaphone,
  MonitorSmartphone,
  Moon,
  Receipt,
  RefreshCw,
  Route as RouteIcon,
  Settings2,
  SlidersHorizontal,
  Smartphone,
  Sun,
  Truck,
  UserRound,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useSync } from '../contexts/SyncContext.tsx';
import { odooConfigAPI } from '../services/api.ts';
import { toast } from '../lib/toast.ts';

type NavigationItem = {
  name: string;
  href: string;
  icon: LucideIcon;
  children?: NavigationItem[];
};

const FLEET: NavigationItem = {
  name: 'Fleet Management',
  href: '#',
  icon: Truck,
  children: [
    { name: 'Fuel', href: '/fleet/fuel', icon: Fuel },
    { name: 'Maintenance', href: '/fleet/maintenance', icon: Wrench },
    { name: 'Toll', href: '/fleet/toll', icon: RouteIcon },
    { name: 'Personal Use', href: '/fleet/personal-use', icon: UserRound },
  ],
};

const CREDIT: NavigationItem = {
  name: 'Credit Limit',
  href: '#',
  icon: CreditCard,
  children: [
    {
      name: 'Credit Limit Apply',
      href: '/credit-limit-apply',
      icon: ClipboardList,
    },
    {
      name: 'Credit Limit Extension',
      href: '/credit-limit-extension',
      icon: ClipboardPlus,
    },
    {
      name: 'Temporary Credit',
      href: '/temporary-credit',
      icon: ClipboardCheck,
    },
  ],
};

type NavigationSection = { label?: string; items: NavigationItem[] };

// Records every module shares, in the order they are listed.
const RECORDS: NavigationItem[] = [
  { name: 'Sales', href: '/sales', icon: BarChart3 },
  FLEET,
  { name: 'Daily Activity', href: '/daily-activity', icon: CalendarCheck },
  CREDIT,
  { name: 'Expense', href: '/expense', icon: Receipt },
  { name: 'Reports', href: '/reports', icon: FileText },
];

const DASHBOARD: NavigationItem = {
  name: 'Dashboard',
  href: '/dashboard',
  icon: Home,
};
const ADVERTISEMENT: NavigationItem = {
  name: 'Advertisement',
  href: '/advertisement',
  icon: Megaphone,
};
// Who is on the app, who on the web, and on which version.
const USER_ACTIVITY: NavigationItem = {
  name: 'User Activity',
  href: '/user-activity',
  icon: MonitorSmartphone,
};
const SETTINGS: NavigationItem = {
  name: 'Settings',
  href: '/settings',
  icon: SlidersHorizontal,
};
// Writes waiting for, or refused by, Odoo.
const ODOO_QUEUE: NavigationItem = {
  name: 'Odoo Queue',
  href: '/odoo-queue',
  icon: ListChecks,
};

const getNavigation = (role: string): NavigationSection[] => {
  if (role === 'admin') {
    return [
      { items: [DASHBOARD] },
      {
        label: 'People',
        items: [
          { name: 'Users', href: '/employees', icon: Users },
          USER_ACTIVITY,
          { name: 'Manage Notifications', href: '/notifications', icon: Bell },
        ],
      },
      { label: 'Records', items: RECORDS },
      {
        label: 'Setup',
        items: [
          ADVERTISEMENT,
          { name: 'Configuration', href: '/configuration', icon: Cog },
          SETTINGS,
          ODOO_QUEUE,
        ],
      },
    ];
  }

  if (role === 'super_admin') {
    return [
      { items: [DASHBOARD] },
      {
        label: 'Manage',
        items: [
          { name: 'Projects', href: '/projects', icon: FolderKanban },
          USER_ACTIVITY,
          { name: 'App Updates', href: '/app-updates', icon: Smartphone },
        ],
      },
      { label: 'Records', items: RECORDS },
      {
        label: 'Setup',
        items: [
          ADVERTISEMENT,
          {
            name: 'Odoo Configurations',
            href: '/odoo-configurations',
            icon: Settings2,
          },
          SETTINGS,
          ODOO_QUEUE,
        ],
      },
    ];
  }

  return [{ items: [DASHBOARD, ADVERTISEMENT] }];
};

const NAV_WIDTH = 264;
const RAIL_WIDTH = 68;

// Every screen mounts its own Layout, so what must outlive a change of
// screen is kept here: how far the menu is scrolled and which groups are open.
let menuScroll = 0;
const openGroups = new Set<string>();

const isActive = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(`${href}/`);

export const Layout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, project, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { syncStatus, isSyncing, startSync, updateSync, endSync } = useSync();

  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
    useDisclosure();
  // On a wide screen the menu narrows to a strip of icons; the choice is kept.
  const [rail, setRail] = useLocalStorage({
    key: 'admin-menu-rail',
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const [, redraw] = useState(0);
  const menuViewport = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    menuViewport.current?.scrollTo({ top: menuScroll });
  }, []);

  const navigation = useMemo(
    () => getNavigation(user?.role || ''),
    [user?.role],
  );
  const configId = project?.odoo_configuration_id;
  const isDark = theme === 'dark';

  const activeTitle = useMemo(() => {
    for (const item of navigation.flatMap((section) => section.items)) {
      const child = item.children?.find((sub) => isActive(pathname, sub.href));
      if (child) return child.name;
      if (isActive(pathname, item.href)) return item.name;
    }
    return 'Admin Panel';
  }, [navigation, pathname]);

  const handleLogout = async () => {
    try {
      if (isSyncing) {
        toast.error('Please wait until the sync is completed.');
        return;
      }

      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSync = () => {
    if (!configId || isSyncing) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let url = odooConfigAPI.getAuthorizationStream(configId);

      // Add token as query parameter
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}token=${token}`;

      // Begin syncing with an initial message
      startSync('Connecting to Odoo...');

      // Create the EventSource to listen to server-sent events
      const eventSource = new EventSource(url);

      // When a message is received, update the sync message
      eventSource.onmessage = (event) => {
        const data = event.data;

        // Check if this is the completion message
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'complete') {
            if (parsed.success) toast.success('Data synced successfully');
            else toast.error(`Sync completed with ${parsed.errors} errors`);
            eventSource.close();
            endSync();
            return;
          }
        } catch {
          // Not JSON, just a status message
        }

        updateSync(data);
      };

      // Handle errors from the event stream
      eventSource.onerror = (error) => {
        console.error('Sync error:', error);
        toast.error('Failed to connect to sync stream');
        eventSource.close();
        endSync();
      };
    } catch (error: any) {
      console.error('Sync error:', error);
      toast.error(error.message);
      endSync();
    }
  };

  const showBrand = user?.role === 'admin' && project;
  const roleName = user?.role?.replace('_', ' ') || 'User';
  // The strip of icons is for wide screens; a phone's drawer is always full.
  const railed = rail && !mobileOpened;
  const rounded = { borderRadius: 'var(--mantine-radius-md)' };

  const toggleGroup = (name: string) => {
    if (openGroups.has(name)) openGroups.delete(name);
    else openGroups.add(name);
    redraw((count) => count + 1);
  };

  // One entry of the full-width menu.
  const wideItem = (item: NavigationItem) => {
    if (!item.children) {
      return (
        <NavLink
          key={item.name}
          component={Link}
          to={item.href}
          label={item.name}
          leftSection={<item.icon size={18} />}
          active={isActive(pathname, item.href)}
          onClick={closeMobile}
          style={rounded}
        />
      );
    }

    const current = item.children.some((sub) => isActive(pathname, sub.href));
    return (
      <NavLink
        key={item.name}
        label={item.name}
        leftSection={<item.icon size={18} />}
        childrenOffset={28}
        // Open while one of its screens is shown, or when opened by hand.
        opened={current || openGroups.has(item.name)}
        onChange={() => toggleGroup(item.name)}
        c={current ? 'var(--mantine-primary-color-light-color)' : undefined}
        style={rounded}
      >
        {item.children.map((sub) => (
          <NavLink
            key={sub.name}
            component={Link}
            to={sub.href}
            label={sub.name}
            leftSection={<sub.icon size={16} />}
            active={isActive(pathname, sub.href)}
            onClick={closeMobile}
            style={rounded}
          />
        ))}
      </NavLink>
    );
  };

  // The same entry as an icon, with its name on hover.
  const railItem = (item: NavigationItem) => {
    if (!item.children) {
      const active = isActive(pathname, item.href);
      return (
        <Tooltip key={item.name} label={item.name} position="right">
          <ActionIcon
            component={Link}
            to={item.href}
            variant={active ? 'light' : 'subtle'}
            color={active ? undefined : 'gray'}
            size={44}
            aria-label={item.name}
          >
            <item.icon size={20} />
          </ActionIcon>
        </Tooltip>
      );
    }

    const current = item.children.some((sub) => isActive(pathname, sub.href));
    return (
      <Menu
        key={item.name}
        trigger="click-hover"
        position="right-start"
        offset={12}
        withinPortal
      >
        <Menu.Target>
          <ActionIcon
            variant={current ? 'light' : 'subtle'}
            color={current ? undefined : 'gray'}
            size={44}
            aria-label={item.name}
          >
            <item.icon size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{item.name}</Menu.Label>
          {item.children.map((sub) => (
            <Menu.Item
              key={sub.name}
              component={Link}
              to={sub.href}
              leftSection={<sub.icon size={16} />}
              color={isActive(pathname, sub.href) ? 'green' : undefined}
            >
              {sub.name}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  };

  return (
    <AppShell
      // "alt": the sidebar runs the full height on the left, and the header
      // sits to its right, above the content.
      layout="alt"
      header={{ height: 60 }}
      navbar={{
        width: { base: NAV_WIDTH, sm: rail ? RAIL_WIDTH : NAV_WIDTH },
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" gap="sm">
          <Burger
            opened={mobileOpened}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
            aria-label="Toggle navigation"
          />
          <Burger
            opened={false}
            onClick={() => setRail((current) => !current)}
            visibleFrom="sm"
            size="sm"
            aria-label={rail ? 'Widen the menu' : 'Narrow the menu'}
          />
          <Text fw={500} size="sm" c="dimmed" truncate flex={1}>
            {activeTitle}
          </Text>

          {user?.role === 'admin' && configId && (
            <Button
              variant="light"
              size="xs"
              leftSection={<RefreshCw size={14} />}
              onClick={handleSync}
              loading={isSyncing}
            >
              Sync Data
            </Button>
          )}

          <Tooltip label={isDark ? 'Light mode' : 'Dark mode'}>
            <ActionIcon
              variant="default"
              size="lg"
              aria-label="Toggle theme"
              onClick={toggleTheme}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <AppShell.Section px="sm" h={60}>
          <Group
            h="100%"
            gap="xs"
            wrap="nowrap"
            justify={railed ? 'center' : 'flex-start'}
          >
            <Burger
              opened={mobileOpened}
              onClick={toggleMobile}
              hiddenFrom="sm"
              size="sm"
              aria-label="Close navigation"
            />
            {/* The project's logo; Suncrop's when it has none, fails to load,
                or there is no single project (super admin). */}
            <Image
              src={showBrand && project.logo ? `${project.logo}` : '/suncrop-logo.png'}
              fallbackSrc="/suncrop-logo.png"
              alt={showBrand ? project.name : 'Suncrop'}
              w={32}
              h={32}
              fit="contain"
            />
            {!railed && (
              <Box miw={0}>
                <Text fw={600} truncate lh={1.2}>
                  {showBrand ? project.name : 'TeamSol'}
                </Text>
                <Text size="xs" c="dimmed" lh={1.2}>
                  Admin Panel
                </Text>
              </Box>
            )}
          </Group>
        </AppShell.Section>

        <Divider />

        <AppShell.Section
          grow
          component={ScrollArea}
          viewportRef={menuViewport}
          onScrollPositionChange={({ y }: { y: number }) => {
            menuScroll = y;
          }}
          px="sm"
          py="sm"
          type="scroll"
        >
          <Stack gap="md">
            {navigation.map((section, index) => (
              <Stack
                key={section.label || index}
                gap={railed ? 4 : 2}
                align={railed ? 'center' : 'stretch'}
              >
                {section.label &&
                  (railed ? (
                    <Divider mb={6} w="100%" />
                  ) : (
                    <Text
                      size="xs"
                      fw={600}
                      c="dimmed"
                      tt="uppercase"
                      px="sm"
                      mb={4}
                      style={{ letterSpacing: '0.04em' }}
                    >
                      {section.label}
                    </Text>
                  ))}
                {section.items.map((item) =>
                  railed ? railItem(item) : wideItem(item),
                )}
              </Stack>
            ))}
          </Stack>
        </AppShell.Section>

        <Divider />

        <AppShell.Section p="sm">
          {railed ? (
            <Stack gap="xs" align="center">
              <Tooltip
                label={`${user?.name || 'User'} · ${roleName}`}
                position="right"
              >
                <Avatar radius="md" color="initials" name={user?.name} />
              </Tooltip>
              <Tooltip label="Sign out" position="right">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  aria-label="Sign out"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                </ActionIcon>
              </Tooltip>
            </Stack>
          ) : (
            <Group gap="sm" wrap="nowrap">
              <Avatar radius="md" color="initials" name={user?.name} />
              <Box miw={0} flex={1}>
                <Text size="sm" fw={500} truncate>
                  {user?.name}
                </Text>
                <Text size="xs" c="dimmed" tt="capitalize" truncate>
                  {roleName}
                </Text>
              </Box>
              <Tooltip label="Sign out">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  aria-label="Sign out"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-8))">
        {children}

        {/* Progress of a running Odoo data sync */}
        {isSyncing && (
          <Paper
            pos="fixed"
            bottom={16}
            right={16}
            maw={420}
            p="sm"
            shadow="lg"
            withBorder
            style={{ zIndex: 200 }}
          >
            <Group gap="sm" wrap="nowrap">
              <Loader size="sm" />
              <Text size="sm">{syncStatus}</Text>
            </Group>
          </Paper>
        )}
      </AppShell.Main>
    </AppShell>
  );
};
